import * as React from 'react';
import { useIntl } from 'react-intl';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';

import { CellBadge, DataTable, DataTableColumnHeader } from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { commonMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { chargesMessages } from 'src/features/charges/utils/messages';
import {
    CHARGE_CATEGORY_BADGE_OPTIONS,
    CHARGE_STATUS_BADGE_OPTIONS,
    CHARGE_TYPE_BADGE_OPTIONS,
} from 'src/features/charges/utils/constants';
import { billingSimulationMessages } from 'src/features/billingSimulation/utils/messages';
import { SimulationCharge } from 'src/features/billingSimulation/utils/types';

type BillingSimulationChargesTableProps = {
    charges: SimulationCharge[];
    isLoading?: boolean;
};

const BillingSimulationChargesTable = ({ charges, isLoading }: BillingSimulationChargesTableProps) => {
    const { formatMessage, formatNumber } = useIntl();

    const columnHelper = React.useMemo(() => createColumnHelper<SimulationCharge>(), []);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => <div className="whitespace-nowrap">{row.original.name}</div>,
                meta: { title: formatMessage(commonMessages.name) },
            }),
            columnHelper.accessor('service_plan_item_name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.service_plan_item_name || '-',
                meta: { title: formatMessage(chargesMessages.item) },
            }),
            columnHelper.accessor('type', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellBadge,
                meta: {
                    title: formatMessage(commonMessages.type),
                    badgeOptions: CHARGE_TYPE_BADGE_OPTIONS,
                },
            }),
            columnHelper.accessor('category', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellBadge,
                meta: {
                    title: formatMessage(chargesMessages.category),
                    badgeOptions: CHARGE_CATEGORY_BADGE_OPTIONS,
                },
            }),
            columnHelper.accessor('amount', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) =>
                    formatNumber(row.original.amount / 100, {
                        style: 'currency',
                        currency: row.original.currency,
                    }),
                meta: { title: formatMessage(paymentsMessages.amount) },
            }),
            columnHelper.accessor('tax_amount', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) =>
                    formatNumber(row.original.tax_amount / 100, {
                        style: 'currency',
                        currency: row.original.currency,
                    }),
                meta: { title: formatMessage(paymentsMessages.taxAmount) },
            }),
            columnHelper.accessor('amount_with_tax', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) =>
                    formatNumber(row.original.amount_with_tax / 100, {
                        style: 'currency',
                        currency: row.original.currency,
                    }),
                meta: { title: formatMessage(paymentsMessages.amountWithTax) },
            }),
            columnHelper.accessor('status', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellBadge,
                meta: {
                    title: formatMessage(chargesMessages.status),
                    badgeOptions: CHARGE_STATUS_BADGE_OPTIONS,
                },
            }),
            columnHelper.accessor('billing_period_start', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDate,
                meta: { title: formatMessage(chargesMessages.periodStart) },
            }),
            columnHelper.accessor('billing_period_end', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: CellDate,
                meta: { title: formatMessage(chargesMessages.periodEnd) },
            }),
        ],
        [columnHelper, formatMessage, formatNumber],
    );

    const data = React.useMemo(
        () => ({ count: charges.length, next: null, previous: null, results: charges }),
        [charges],
    );

    return (
        <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            enableSearchParams={false}
            getSortedRowModel={getSortedRowModel()}
            getRowId={(row) => row.id}
            semantics={{
                title: formatMessage(chargesMessages.charges),
                entryName: formatMessage(chargesMessages.charge).toLowerCase(),
                entryNames: formatMessage(chargesMessages.charges).toLowerCase(),
                messageBlockSubtitle: formatMessage(billingSimulationMessages.noCharges),
            }}
        />
    );
};

export default BillingSimulationChargesTable;
